import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../button';

function Modal({
  title,
  onClose,
  backdrop = true,
  children,
  footer,
  position = 'middle',
  transition = 'ease-in-out',
  duration = 300,
  isOpen = true,
  showCloseButton = true,
  footerPosition = 'right',
  containerClz,
  childrenClassName,
  backdropClz,
}) {
  const [isClosing, setIsClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  const timerRef = useRef(null);

  // Keep ref in sync with latest onClose without adding it to effect deps
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      // Cancel any pending close timer and reset closing state when reopening
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setIsClosing(false);
    } else {
      document.body.classList.remove('modal-open');
      setIsClosing(true);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setIsClosing(false);
        onCloseRef.current();
      }, duration);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration, isOpen]);

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`${backdrop ? 'bg-black bg-opacity-50' : ''} fixed inset-0 ${backdropClz || ''}`}
        onClick={handleBackdropClick}
      ></div>
      <div
        className={`${
          position === 'middle'
            ? 'relative shrink-0'
            : position === 'left'
              ? 'fixed left-0 top-0 bottom-0'
              : 'fixed right-0 top-0 bottom-0'
        } ${
          position === 'middle' ? 'md:min-w-[400px]' : ''
        } bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${transition} ${
          isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        } ${containerClz ? containerClz : ''}`}
      >
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-4 ">
            {title && (
              <Fragment>
                {typeof title === 'string' ? (
                  <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                ) : (
                  title
                )}
              </Fragment>
            )}
            {showCloseButton && (
              <Button
                variant="secondaryContained"
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.71967 5.71967C6.01256 5.42678 6.48744 5.42678 6.78033 5.71967L12 10.9393L17.2197 5.71967C17.5126 5.42678 17.9874 5.42678 18.2803 5.71967C18.5732 6.01256 18.5732 6.48744 18.2803 6.78033L13.0607 12L18.2803 17.2197C18.5732 17.5126 18.5732 17.9874 18.2803 18.2803C17.9874 18.5732 17.5126 18.5732 17.2197 18.2803L12 13.0607L6.78033 18.2803C6.48744 18.5732 6.01256 18.5732 5.71967 18.2803C5.42678 17.9874 5.42678 17.5126 5.71967 17.2197L10.9393 12L5.71967 6.78033C5.42678 6.48744 5.42678 6.01256 5.71967 5.71967Z"
                      fill="#FF8C00"
                      stroke="#FF8C00"
                      strokeLinecap="round"
                    />
                  </svg>
                }
                onClick={onClose}
              />
            )}
          </div>
        )}
        {title && <hr className="mx-4 border-gray-200" />}
        <div
          className={`md:p-6 p-4 ${childrenClassName ? childrenClassName : ''}`}
        >
          {children}
        </div>
        {footer && (
          <div
            className={`${
              footerPosition === 'left'
                ? 'justify-start'
                : footerPosition === 'right'
                  ? 'justify-end'
                  : 'justify-center'
            } p-4 flex`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  const modalRoot =
    document.getElementById('storybook-root') ||
    document.getElementById('modal-root');

  if (!modalRoot) {
    console.error(
      "Error: Could not find element with id 'modal-root'. Make sure it exists in the DOM."
    );
    return null;
  }

  return createPortal(isOpen ? modal : null, modalRoot);
}

Modal.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onClose: PropTypes.func.isRequired,
  backdrop: PropTypes.bool,
  children: PropTypes.node.isRequired,
  footer: PropTypes.element,
  position: PropTypes.oneOf(['left', 'right', 'middle']),
  transition: PropTypes.string,
  duration: PropTypes.number,
  isOpen: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  footerPosition: PropTypes.oneOf(['left', 'right']),
  containerClz: PropTypes.string,
  childrenClassName: PropTypes.string,
  backdropClz: PropTypes.string,
};

export default Modal;
